/**
 * Toast manager backed by Sonner (replaces Chakra useToast).
 * No init() required; uses Sonner directly for consistency with shadcn/sonner.
 */
import { toast as sonnerToast } from 'sonner';

interface ToastCall {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
  timestamp: number;
}

export interface ToastConfig {
  title?: string;
  description?: string;
  status?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  isClosable?: boolean;
  position?:
    | 'top'
    | 'top-left'
    | 'top-right'
    | 'bottom'
    | 'bottom-left'
    | 'bottom-right';
  variant?: string;
  id?: string;
}

export interface ToastMessageOptions {
  description?: string;
  duration?: number;
}

const DEDUPLICATION_WINDOW = 2000;
const MAX_RECENT_TOASTS = 100;

function createToastManager() {
  const recentToasts: ToastCall[] = [];

  function shouldShowToast(message: string, type: ToastCall['type']): boolean {
    const now = Date.now();
    const filtered = recentToasts.filter(
      (t) => now - t.timestamp < DEDUPLICATION_WINDOW
    );
    recentToasts.length = 0;
    recentToasts.push(...filtered);

    const isDuplicate = recentToasts.some(
      (t) => t.message === message && t.type === type
    );
    if (!isDuplicate) {
      recentToasts.push({ message, type, timestamp: now });
      if (recentToasts.length > MAX_RECENT_TOASTS) {
        recentToasts.splice(0, recentToasts.length - MAX_RECENT_TOASTS);
      }
      return true;
    }
    return false;
  }

  function show(config: ToastConfig) {
    const { title, description, status = 'info', duration = 5000 } = config;
    const primary = title ?? description ?? '';
    if (!primary) return;
    if (!shouldShowToast(primary, status)) return;

    const secondary = title && description ? description : undefined;
    const opts = { description: secondary, duration };
    switch (status) {
      case 'success':
        sonnerToast.success(primary, opts);
        break;
      case 'error':
        sonnerToast.error(primary, opts);
        break;
      case 'warning':
        sonnerToast.warning(primary, opts);
        break;
      default:
        sonnerToast(primary, opts);
    }
  }

  return {
    show,
    success(message: string, options?: ToastMessageOptions) {
      show({
        title: 'Success',
        description: message,
        status: 'success',
        duration: options?.duration ?? 5000,
        isClosable: true,
      });
    },
    error(message: string, options?: ToastMessageOptions) {
      show({
        title: message,
        description: options?.description,
        status: 'error',
        duration: options?.duration ?? 5000,
        isClosable: true,
      });
    },
    info(message: string, options?: ToastMessageOptions) {
      show({
        title: 'Info',
        description: message,
        status: 'info',
        duration: options?.duration ?? 5000,
        isClosable: true,
      });
    },
    warning(message: string, options?: ToastMessageOptions) {
      show({
        title: 'Warning',
        description: message,
        status: 'warning',
        duration: options?.duration ?? 5000,
        isClosable: true,
      });
    },
    /** No-op for backward compatibility; Sonner is used directly. */
    init(_fn: (config: ToastConfig) => void) {},
  };
}

export const toast = createToastManager();
